import React, { useState, useEffect, useContext, useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AuthContext, IntlContext } from "context";
import { useLocation, useNavigate, useParams } from "react-router";
import { Link } from "react-router-dom";
import { showOrMaskConversation, fetchArchivedConversations } from "hooks/thread";
import type { ThreadConversationInterface } from "types/thread";
import { Role } from "types/user";
import Image from "components/File/Image";
import moment from "moment";
import { Loader } from "ui/Loader";
import { NotificationContext } from "app/Notification/Context";
import ConfirmModal from "ui/Modal/ConfirmModal";
import { buildRoute } from "routes";
import { Archive, MapPin, Search, ChevronDown, MessageCircleMore } from "lucide-react";
import { VerifiedProfileBadge } from "components/provider/VerifiedProfileBadge";
import { toast } from "sonner";
interface Props {
    data: Array<ThreadConversationInterface>;
    refreshDataConversation: () => void;
    isLoading: boolean;
}

interface ConversationItem {
    id: string;
    service_id: string;
    client_id: string;
    user_id: string;
    client: { user_id: string; first_name: string; last_name: string };
    provider: {
        user_id: string;
        first_name: string;
        last_name: string;
        city: string | null;
        postcode: string | null;
        is_verified: boolean;
    };
    photo?: string;
    first_name?: string;
    last_name?: string;
    category: string | null;
    subcategory?: string | null;
    hourly_rate?: string | null;
    service_is_active?: boolean;
    city?: string | null;
    postcode?: string | null;
    is_verified?: boolean;
    last_message: string;
    last_message_created_at: string;
    unread_count: number;
    show_message_client: boolean;
    show_message_provider: boolean;
    mask_message_client: boolean;
    mask_message_provider: boolean;
}

const ListMessage: React.FC<Props> = ({ data, refreshDataConversation, isLoading }) => {
    const { service_id: serviceId } = useParams() as { service_id: string };
    const { client_id: clientId } = useParams() as { client_id: string };
    const auth = useContext(AuthContext);
    const intl = useContext(IntlContext);
    const { refreshThreads } = useContext(NotificationContext);
    const queryClient = useQueryClient();
    const [showUnarchiveModal, setShowUnarchiveModal] = useState<boolean>(false);
    const [unarchiveMessageId, setUnarchiveMessageId] = useState<string>("");
    const [allConversation, setAllConversation] = useState<ConversationItem[]>([]);
    const [search, setSearch] = useState<string>("");
    const [archivedExpanded, setArchivedExpanded] = useState<boolean>(false);
    const navigate = useNavigate();
    const location = useLocation();

    const isClient = auth.currentRole === Role.CLIENT;

    const {
        data: archivedData,
        isPending: isArchivedPending,
        isError: isArchivedError,
        refetch: refetchArchived,
    } = useQuery({
        queryKey: ["archived-conversations"],
        queryFn: fetchArchivedConversations,
        staleTime: 30_000,
    });

    const checkIdentityOfUserList = useCallback(
        (conversation: ThreadConversationInterface) => {
            if (!conversation) {
                return;
            }

            if (auth.account?.user.id === conversation.client.user_id) {
                return conversation.provider;
            }

            return conversation.client;
        },
        [auth.account?.user.id],
    );

    const markConversationAsReadInCache = (nextServiceId: string, nextClientId: string) => {
        queryClient.setQueryData<ThreadConversationInterface[]>(["all-conversations"], (conversations) =>
            conversations?.map((conversation) =>
                conversation.service_id === nextServiceId && conversation.client_id === nextClientId
                    ? { ...conversation, unread_count: 0 }
                    : conversation,
            ),
        );
    };

    const handleChangeConversation = async (messageId: string, nextServiceId: string, nextClientId: string) => {
        if (location.pathname.includes("mes-reservations")) {
            navigate(buildRoute.myReservationMessages(nextServiceId, nextClientId));
        } else {
            navigate(buildRoute.messaging(nextServiceId, nextClientId));
        }

        markConversationAsReadInCache(nextServiceId, nextClientId);
        void queryClient.invalidateQueries({ queryKey: ["thread", nextServiceId, nextClientId] });

        await showOrMaskConversation(messageId, {
            show_or_mask: "show",
        });
    };

    const handleShowUnarchiveModal = (conversationId: string, e: React.MouseEvent) => {
        e.stopPropagation();
        setUnarchiveMessageId(conversationId);
        setShowUnarchiveModal(true);
    };

    const handleUnarchiveConversation = async () => {
        if (!unarchiveMessageId) {
            setShowUnarchiveModal(false);
            return;
        }

        try {
            await showOrMaskConversation(unarchiveMessageId, {
                show_or_mask: "show",
            });
            setShowUnarchiveModal(false);
            refreshDataConversation();
            refreshThreads();
            void refetchArchived();
            await queryClient.refetchQueries({ queryKey: ["all-conversations"] });
        } catch (err) {
            setShowUnarchiveModal(false);
            toast.error(intl.formatMessage("booking.messages.error.unarchive_failed"));
        }
    };

    useEffect(() => {
        if (data) {
            const conversations = [...data].reduce((memo: ConversationItem[], curVal) => {
                const dataUser = checkIdentityOfUserList(curVal);

                memo.push({
                    id: curVal.id,
                    service_id: curVal.service_id,
                    client_id: curVal.client_id,
                    user_id: curVal.user_id,
                    client: curVal.client,
                    provider: curVal.provider,
                    photo: dataUser?.photo,
                    first_name: dataUser?.first_name,
                    last_name: dataUser?.last_name,
                    category: curVal.service?.category ?? null,
                    subcategory: curVal.service?.subcategory ?? null,
                    hourly_rate: curVal.service?.hourly_rate ?? null,
                    service_is_active: curVal.service?.is_active,
                    city: curVal.provider.city ?? null,
                    postcode: curVal.provider.postcode ?? null,
                    is_verified: curVal.provider.is_verified,
                    last_message: curVal.message,
                    last_message_created_at: curVal.created_at,
                    unread_count: curVal.unread_count,
                    show_message_client: curVal.show_message_client,
                    show_message_provider: curVal.show_message_provider,
                    mask_message_client: curVal.mask_message_client,
                    mask_message_provider: curVal.mask_message_provider,
                });

                return memo;
            }, []);

            setAllConversation([...conversations]);
        }
    }, [checkIdentityOfUserList, data]);

    useEffect(() => {
        const timer = setTimeout(() => {
            refreshDataConversation();
        }, 30000);

        return () => clearTimeout(timer);
    }, [refreshDataConversation]);

    const formatCategoryLabel = (category: string | null, subcategory?: string | null): string => {
        if (!category) {
            return "";
        }

        const categoryKey = String(category);
        const cat = intl.formatMessage(`service.label.category.${categoryKey}`);

        if (!subcategory) {
            return cat;
        }

        return `${cat} · ${intl.formatMessage(
            `subcategory.${categoryKey.toLowerCase()}.${String(subcategory).toLowerCase()}.label`,
        )}`;
    };

    const filteredConversations = allConversation.filter((conv) => {
        if (!search.trim()) return true;
        const q = search.toLowerCase();
        const contactName = `${conv.first_name ?? ""} ${conv.last_name ?? ""}`.toLowerCase();
        const categoryLabel = formatCategoryLabel(conv.category, conv.subcategory).toLowerCase();
        const lastMessage = (conv.last_message ?? "").toLowerCase();
        return contactName.includes(q) || categoryLabel.includes(q) || lastMessage.includes(q);
    });

    if (isLoading) {
        return <Loader />;
    }

    const archivedConversations: ConversationItem[] = (archivedData ?? []).map((curVal) => {
        const dataUser = checkIdentityOfUserList(curVal);
        return {
            id: curVal.id,
            service_id: curVal.service_id,
            client_id: curVal.client_id,
            user_id: curVal.user_id,
            client: curVal.client,
            provider: curVal.provider,
            photo: dataUser?.photo,
            first_name: dataUser?.first_name,
            last_name: dataUser?.last_name,
            category: curVal.service?.category ?? null,
            subcategory: curVal.service?.subcategory ?? null,
            hourly_rate: curVal.service?.hourly_rate ?? null,
            service_is_active: curVal.service?.is_active,
            city: curVal.provider.city ?? null,
            postcode: curVal.provider.postcode ?? null,
            is_verified: curVal.provider.is_verified,
            last_message: curVal.message,
            last_message_created_at: curVal.created_at,
            unread_count: curVal.unread_count,
            show_message_client: curVal.show_message_client,
            show_message_provider: curVal.show_message_provider,
            mask_message_client: curVal.mask_message_client,
            mask_message_provider: curVal.mask_message_provider,
        };
    });

    const renderConversationItem = (conversation: ConversationItem, index: number, archived = false) => {
        const dateMoment = conversation.last_message_created_at
            ? moment().diff(moment(conversation.last_message_created_at), "hours") >= 24
                ? moment(conversation.last_message_created_at).format("D MMM YYYY")
                : moment(conversation.last_message_created_at).fromNow()
            : "";
        const hasUnread = conversation.unread_count > 0;
        const isActive = conversation.service_id === serviceId && conversation.client_id === clientId;
        const locationLabel =
            conversation.city && conversation.postcode
                ? `${conversation.city} (${conversation.postcode})`
                : (conversation.city ?? null);
        const initials = `${(conversation.first_name ?? "").charAt(0)}${(conversation.last_name ?? "").charAt(0)}`;

        const contactName = `${conversation.first_name ?? ""} ${conversation.last_name?.charAt(0) ?? ""}.`;
        const itemAriaLabel =
            hasUnread && !archived
                ? intl
                      .formatMessage("booking.messages.label.conversation_with_unread")
                      .replace(":name", contactName)
                      .replace(":count", String(conversation.unread_count))
                : intl.formatMessage("booking.messages.label.conversation_with").replace(":name", contactName);

        const handleKeyDown = (e: React.KeyboardEvent<HTMLLIElement>) => {
            if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                if (!archived) {
                    void handleChangeConversation(conversation.id, conversation.service_id, conversation.client_id);
                } else {
                    navigate(buildRoute.messaging(conversation.service_id, conversation.client_id));
                }
            }
        };

        const serviceLabel = formatCategoryLabel(conversation.category, conversation.subcategory);
        const canLinkToProvider = isClient && conversation.service_is_active !== false;
        const providerUrl = buildRoute.provider(conversation.service_id);

        const avatarContent = (
            <div className="w-11 h-11 rounded-full overflow-hidden bg-secondary flex items-center justify-center text-sm font-semibold text-secondary-foreground">
                {conversation.photo ? (
                    <Image id={conversation.photo} className="w-full h-full object-cover" />
                ) : (
                    <span>{initials}</span>
                )}
            </div>
        );

        const nameContent = (
            <>
                {conversation.first_name} {conversation.last_name?.charAt(0)}.
            </>
        );

        const nameClassName = `text-sm truncate ${hasUnread && !archived ? "font-bold text-foreground" : "font-semibold text-foreground"}`;

        return (
            <li
                key={`${archived ? "archived" : "active"}-${index}`}
                tabIndex={0}
                aria-label={itemAriaLabel}
                aria-current={isActive ? "true" : undefined}
                className={`relative flex items-start gap-3 px-3 py-3.5 cursor-pointer border-b border-border transition-colors group focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-inset ${
                    archived
                        ? "bg-secondary/20 hover:bg-secondary/40 opacity-75"
                        : isActive
                          ? "bg-primary/5 border-l-2 border-l-primary"
                          : hasUnread
                            ? "bg-white hover:bg-secondary/50"
                            : "bg-white hover:bg-secondary/30"
                }`}
                onClick={() => {
                    if (!archived) {
                        void handleChangeConversation(conversation.id, conversation.service_id, conversation.client_id);
                    } else {
                        navigate(buildRoute.messaging(conversation.service_id, conversation.client_id));
                    }
                }}
                onKeyDown={handleKeyDown}
            >
                {canLinkToProvider ? (
                    <Link
                        to={providerUrl}
                        className="relative flex-shrink-0 rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                        onClick={(e) => e.stopPropagation()}
                        aria-label={intl.formatMessage("booking.messages.label.view_profile")}
                    >
                        {avatarContent}
                    </Link>
                ) : (
                    <div className="relative flex-shrink-0" aria-hidden="true">
                        {avatarContent}
                    </div>
                )}

                <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 mb-0.5">
                        <div className="flex items-center gap-1.5 min-w-0">
                            {canLinkToProvider ? (
                                <Link
                                    to={providerUrl}
                                    className={`${nameClassName} hover:underline`}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    {nameContent}
                                </Link>
                            ) : (
                                <span className={nameClassName}>{nameContent}</span>
                            )}
                            {conversation.is_verified && <VerifiedProfileBadge />}
                        </div>
                        <div className="flex items-center gap-1.5 flex-shrink-0" aria-hidden="true">
                            {dateMoment && <span className="text-xs text-muted-foreground">{dateMoment}</span>}
                        </div>
                    </div>

                    {serviceLabel && (
                        <div className="mb-1">
                            <span
                                className={`text-xs font-semibold ${
                                    conversation.service_is_active !== false ? "text-primary" : "text-muted-foreground"
                                }`}
                            >
                                {serviceLabel}
                            </span>
                        </div>
                    )}

                    <p
                        aria-hidden="true"
                        className={`text-xs truncate mb-1.5 ${hasUnread && !archived ? "text-foreground font-medium" : "text-muted-foreground"}`}
                    >
                        {conversation.last_message}
                    </p>

                    <div aria-hidden="true" className="flex items-center gap-2 flex-wrap">
                        {locationLabel && (
                            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground bg-secondary px-2 py-0.5 rounded-full">
                                <MapPin className="w-2.5 h-2.5" />
                                {locationLabel}
                            </span>
                        )}
                        {conversation.hourly_rate && (
                            <span className="inline-flex items-center text-xs text-[#009966] font-black bg-primary/8 px-2 py-0.5 rounded-full">
                                {conversation.hourly_rate}€/h
                            </span>
                        )}
                    </div>
                </div>

                {archived && (
                    <button
                        type="button"
                        className="opacity-0 group-hover:opacity-100 focus:opacity-100 flex-shrink-0 absolute top-2 right-2 flex items-center gap-1 text-xs text-primary bg-primary/10 hover:bg-primary/20 px-2 py-1 rounded-full transition-all"
                        onClick={(e) => handleShowUnarchiveModal(conversation.id, e)}
                        aria-label={intl.formatMessage("booking.messages.actions.unarchive_discussion")}
                    >
                        <Archive className="w-4 h-4" />
                        <span aria-hidden="true">
                            {intl.formatMessage("booking.messages.actions.unarchive_discussion")}
                        </span>
                    </button>
                )}
            </li>
        );
    };

    return (
        <React.Fragment>
            <ConfirmModal
                show={showUnarchiveModal}
                onClose={() => setShowUnarchiveModal(false)}
                onConfirm={() => {
                    void handleUnarchiveConversation();
                }}
                title={intl.formatMessage("booking.messages.modal.unarchive.label.title")}
                message={intl.formatMessage("booking.messages.modal.unarchive.label.message")}
                confirmButton={intl.formatMessage("generic.action.confirm")}
                cancelButton={intl.formatMessage("generic.action.cancel")}
                zIndexIncrement={100}
            />

            <div className="px-3 py-2.5 border-b border-border bg-white sticky top-0 z-10">
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                    <input
                        type="text"
                        aria-label={intl.formatMessage("booking.messages.label.search_label")}
                        className="w-full pl-9 pr-3 py-2 text-sm bg-secondary/60 border border-transparent rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary focus:bg-white transition-colors placeholder:text-muted-foreground"
                        placeholder={intl.formatMessage("booking.messages.search.placeholder")}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
                {search.trim() && (
                    <p aria-live="polite" className="sr-only">
                        {intl
                            .formatMessage("booking.messages.search.results_count")
                            .replace(":count", String(filteredConversations.length))}
                    </p>
                )}
            </div>

            {filteredConversations.length === 0 && allConversation.length === 0 && (
                <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
                    <div
                        className="w-14 h-14 rounded-full bg-secondary flex items-center justify-center mb-4"
                        aria-hidden="true"
                    >
                        <MessageCircleMore className="w-7 h-7 text-muted-foreground" />
                    </div>
                    <p className="text-sm text-muted-foreground">
                        {isClient
                            ? intl.formatMessage("booking.messages.empty.client")
                            : intl.formatMessage("booking.messages.empty.provider")}
                    </p>
                </div>
            )}

            {filteredConversations.length === 0 && allConversation.length > 0 && (
                <div className="py-8 px-4 text-center">
                    <p className="text-sm text-muted-foreground">
                        {intl.formatMessage("booking.messages.search.no_results")}
                    </p>
                </div>
            )}

            <ul aria-label={intl.formatMessage("booking.messages.label.conversation_list")}>
                {filteredConversations.map((conversation, index) => renderConversationItem(conversation, index, false))}
            </ul>

            {/* Archived section */}
            <button
                type="button"
                className="w-full flex items-center justify-between px-4 py-3 border-t border-border bg-secondary/30 hover:bg-secondary/50 transition-colors text-left"
                onClick={() => setArchivedExpanded((v) => !v)}
                aria-expanded={archivedExpanded}
                aria-controls="archived-conversations-list"
            >
                <span className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                    <Archive className="w-4 h-4" />
                    {intl.formatMessage("booking.messages.section.archived")}
                    {!!archivedData?.length && (
                        <span
                            className="text-xs font-semibold bg-secondary text-secondary-foreground px-2 py-0.5 rounded-full"
                            aria-hidden="true"
                        >
                            {archivedData.length}
                        </span>
                    )}
                </span>
                <ChevronDown
                    aria-hidden="true"
                    className={`w-4 h-4 text-muted-foreground transition-transform ${archivedExpanded ? "rotate-180" : ""}`}
                />
            </button>

            <div id="archived-conversations-list">
                {archivedExpanded && (
                    <div>
                        {isArchivedPending && (
                            <div className="py-6 flex justify-center">
                                <Loader />
                            </div>
                        )}
                        {isArchivedError && (
                            <div className="py-8 px-4 text-center">
                                <p className="text-sm text-muted-foreground">
                                    {intl.formatMessage("booking.messages.error.archived_load_failed")}
                                </p>
                            </div>
                        )}
                        {!isArchivedPending && !isArchivedError && archivedConversations.length === 0 && (
                            <div className="py-8 px-4 text-center">
                                <p className="text-sm text-muted-foreground">
                                    {intl.formatMessage("booking.messages.archived.empty")}
                                </p>
                            </div>
                        )}
                        {!isArchivedPending && !isArchivedError && archivedConversations.length > 0 && (
                            <ul aria-label={intl.formatMessage("booking.messages.label.archived_list")}>
                                {archivedConversations.map((conversation, index) =>
                                    renderConversationItem(conversation, index, true),
                                )}
                            </ul>
                        )}
                    </div>
                )}
            </div>
        </React.Fragment>
    );
};

export default ListMessage;
